import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Sparkles, ArrowRight, Check, Play,
  Film, Palette, LayoutGrid, RefreshCw, Zap, Star, Crown, Globe
} from "lucide-react";
import Footer from "../components/Footer";
import Logo from "../components/Logo";
import { LocaleSwitcher, CurrencySelector } from "../components/LocaleSwitcher";
import { useLocale } from "../context/LocaleContext";

const FEATURE_META = [
  { icon: Sparkles, color: "#c084fc" },
  { icon: Film, color: "#60a5fa" },
  { icon: Palette, color: "#34d399" },
  { icon: LayoutGrid, color: "#f59e0b" },
  { icon: RefreshCw, color: "#fb7185" },
  { icon: Globe, color: "#a78bfa" },
];

const PLAN_META = [
  { id: "starter", icon: Zap, color: "#60a5fa", price: 19, credits: 100, popular: false },
  { id: "growth", icon: Star, color: "#c084fc", price: 49, credits: 280, popular: true },
  { id: "pro", icon: Crown, color: "#fbbf24", price: 99, credits: 600, popular: false },
];

const CREDIT_VALUE_USD = 0.20;

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatPrice } = useLocale();

  const features = t("landing.features", { returnObjects: true });
  const steps = t("landing.steps", { returnObjects: true });
  const tones = t("landing.tones", { returnObjects: true });
  const heroChecks = t("landing.hero.checks", { returnObjects: true });

  return (
    <div className="min-h-screen" style={{ background: "#111111" }}>

      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b"
        style={{ background: "rgba(17,17,17,0.92)", backdropFilter: "blur(12px)", borderColor: "#2a2a2a" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <Logo size="lg" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/about" className="text-zinc-400 hover:text-white text-sm px-4 py-2 transition-colors hidden sm:block">
              {t("nav.about")}
            </Link>
            <LocaleSwitcher />
            <button onClick={() => navigate("/auth")} data-testid="landing-signin-btn"
              className="text-zinc-400 hover:text-white text-sm px-4 py-2 transition-colors">
              {t("nav.signIn")}
            </button>
            <button onClick={() => navigate("/auth")} data-testid="landing-getstarted-btn"
              className="ghost-purple text-sm px-5 py-2 rounded-full font-semibold">
              {t("common.getStarted")}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-24 lg:py-32 overflow-hidden" style={{ background: "#111111" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="space-y-7">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 border text-xs font-semibold"
                style={{ background: "rgba(168,85,247,0.08)", borderColor: "rgba(168,85,247,0.3)", color: "#c084fc" }}>
                <Sparkles className="w-3.5 h-3.5" />
                {t("landing.hero.badge")}
              </div>

              <h1 className="text-5xl lg:text-6xl font-black text-white leading-[1.05]"
                style={{ fontFamily: "Epilogue, sans-serif" }}>
                {t("landing.hero.titleLine1")}<br />
                <span className="gradient-text">{t("landing.hero.titleLine2")}</span>
              </h1>

              <p className="text-lg leading-relaxed max-w-xl" style={{ color: "#a0a0a0" }}>
                {t("landing.hero.subtitle")}
              </p>

              <div className="flex flex-wrap gap-3 pt-1">
                <button onClick={() => navigate("/auth")} data-testid="hero-create-btn"
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white rounded-full px-8 py-3 text-base font-bold transition-colors">
                  {t("landing.hero.cta")} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
                </button>
                <button
                  className="flex items-center gap-2 ghost-purple rounded-full px-6 py-3 text-base font-medium"
                  onClick={() => document.getElementById("how-it-works").scrollIntoView({ behavior: "smooth" })}>
                  <Play className="w-4 h-4" /> {t("landing.hero.seeHow")}
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-6 pt-1 text-sm" style={{ color: "#6b6b6b" }}>
                {heroChecks.map(item => (
                  <div key={item} className="flex items-center gap-1.5">
                    <Check className="w-4 h-4" style={{ color: "#c084fc" }} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl"
                style={{ border: "1px solid #2a2a2a" }}>
                <img
                  src="https://images.unsplash.com/photo-1730871081523-87f829afb1ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxjb250ZW50JTIwY3JlYXRvciUyMGZpbG1pbmclMjB3aXRoJTIwcGhvbmV8ZW58MHx8fHwxNzcyMjI4NDc4fDA&ixlib=rb-4.1.0&q=85"
                  alt="Content creator" className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 rounded-xl p-3"
                  style={{ background: "rgba(26,26,26,0.95)", border: "1px solid #2a2a2a", backdropFilter: "blur(8px)" }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
                    <span className="text-xs font-semibold" style={{ color: "#c084fc" }}>
                      {t("landing.hero.generating")}
                    </span>
                  </div>
                  <div className="w-full rounded-full h-1" style={{ background: "#2a2a2a" }}>
                    <div className="h-1 rounded-full w-3/4 transition-all duration-1000"
                      style={{ background: "linear-gradient(90deg, #9333ea, #c084fc)" }} />
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 flex flex-wrap gap-2 max-w-[180px]">
                {tones.map((tone, i) => (
                  <span key={tone}
                    className={`text-xs px-3 py-1 rounded-full font-medium shadow-sm ${i === 0 ? "bg-purple-600 text-white border border-purple-500" : "border text-zinc-400"}`}
                    style={i !== 0 ? { background: "#1c1c1c", borderColor: "#2d2d2d" } : {}}>
                    {tone}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 border-t" style={{ background: "#161616", borderColor: "#222222" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "Epilogue, sans-serif" }}>
              {t("landing.featuresTitle")}
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: "#6b6b6b" }}>
              {t("landing.featuresSubtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => {
              const { icon: Icon, color } = FEATURE_META[i];
              return (
                <motion.div key={f.title}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="p-6 rounded-2xl" style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${color}14`, border: `1px solid ${color}28` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-2"
                    style={{ fontFamily: "Epilogue, sans-serif" }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#6b6b6b" }}>{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 border-t" style={{ background: "#111111", borderColor: "#222222" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "Epilogue, sans-serif" }}>
              {t("landing.stepsTitle")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <motion.div key={step.title}
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="relative p-6 rounded-2xl" style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                <div className="text-4xl font-black mb-3"
                  style={{ fontFamily: "Epilogue, sans-serif", color: "#3d3d3d" }}>{String(i + 1).padStart(2, "0")}</div>
                <h3 className="text-base font-bold text-white mb-2"
                  style={{ fontFamily: "Epilogue, sans-serif" }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#6b6b6b" }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 border-t" style={{ background: "#161616", borderColor: "#222222" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "Epilogue, sans-serif" }}>
              {t("landing.pricing.title")}
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: "#6b6b6b" }}>
              {t("landing.pricing.subtitle", { creditValue: formatPrice(CREDIT_VALUE_USD) })}
            </p>
          </div>

          <div className="flex justify-center mb-10">
            <CurrencySelector />
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {PLAN_META.map((plan, i) => {
              const Icon = plan.icon;
              const perks = t(`landing.pricing.plans.${plan.id}.perks`, { returnObjects: true });
              return (
                <motion.div key={plan.id}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="relative rounded-2xl p-6 flex flex-col"
                  data-testid={`landing-plan-${plan.id}`}
                  style={{
                    background: plan.popular ? "rgba(124,58,237,0.07)" : "#1c1c1c",
                    border: `1px solid ${plan.popular ? "#7c3aed" : "#2a2a2a"}`,
                  }}>
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full"
                      style={{ background: "#7c3aed", color: "white" }}>
                      {t("landing.pricing.mostPopular")}
                    </span>
                  )}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: `${plan.color}18` }}>
                      <Icon className="w-4 h-4" style={{ color: plan.color }} />
                    </div>
                    <span className="font-bold text-white">{t(`landing.pricing.plans.${plan.id}.label`)}</span>
                  </div>
                  <div className="mb-5">
                    <span className="text-4xl font-black text-white" data-testid={`landing-plan-${plan.id}-price`}>{formatPrice(plan.price, { compact: true })}</span>
                    <span className="text-sm ml-1" style={{ color: "#6b6b6b" }}>{t("landing.pricing.perMonth")}</span>
                    <div className="mt-1 text-sm font-semibold" style={{ color: plan.color }}>
                      {t("landing.pricing.creditsIncluded", { count: plan.credits })}
                    </div>
                  </div>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {perks.map(perk => (
                      <li key={perk} className="flex items-start gap-2 text-sm" style={{ color: "#7a7a7a" }}>
                        <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: plan.color }} />
                        {perk}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => navigate("/auth")}
                    data-testid={`pricing-${plan.id}-btn`}
                    className="w-full py-2.5 rounded-xl text-sm font-bold transition-colors"
                    style={{
                      background: plan.popular ? "#7c3aed" : "#252525",
                      color: plan.popular ? "white" : "#a0a0a0",
                    }}>
                    {t("common.getStarted")}
                  </button>
                </motion.div>
              );
            })}
          </div>

          <p className="text-center text-xs mt-2" style={{ color: "#6b6b6b" }}>
            {t("landing.pricing.footnote")}
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 border-t"
        style={{ background: "linear-gradient(135deg, #1a0a2e 0%, #111111 60%)", borderColor: "#222222" }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4"
            style={{ fontFamily: "Epilogue, sans-serif" }}>
            {t("landing.cta.title")}
          </h2>
          <p className="text-lg mb-8" style={{ color: "#6b6b6b" }}>
            {t("landing.cta.subtitle")}
          </p>
          <button onClick={() => navigate("/auth")} data-testid="landing-cta-final-btn"
            className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-10 py-4 text-lg font-bold inline-flex items-center gap-2 transition-colors">
            {t("common.getStarted")} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
